import {
  Injectable,
  NgZone,
  OnDestroy,
  effect,
  inject,
  signal,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { LockWebService } from "./lock-web.service";
import { SesionResetRegistry } from "./sesion-reset-registry.service";
import {
  SesionActivaComponent,
  SesionActivaDialogData,
} from "@shared/components/sesion-activa/sesion-activa.component";

/** Silencio del usuario que dispara el diálogo. */
const INACTIVIDAD_MS_DEFAULT = 300_000;

/** Tiempo para confirmar presencia antes de soltar el lock. */
const RESPUESTA_MS_DEFAULT = 30_000;

/**
 * Cada cuánto se renueva el lock en silencio mientras el usuario trabaja. Es su propia
 * constante y es mayor que `INACTIVIDAD_MS_DEFAULT` a propósito: así el diálogo siempre
 * llega primero cuando el usuario se queda quieto desde que tomó el lock, y la renovación
 * silenciosa nunca le extiende el lock a alguien que en los hechos ya se fue.
 */
const HEARTBEAT_MS_DEFAULT = 360_000;

const TICK_MS = 1_000;

/**
 * Margen para que los resets de pantalla liberen por su propio camino (el `afterClosed`
 * de un diálogo no es sincrónico) antes de que la red final barra lo que quedó.
 */
const MS_GRACIA_RESET = 300;

const EVENTOS_ACTIVIDAD = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
] as const;

/**
 * Vigila la inactividad del usuario mientras haya locks tomados por esta pestaña y, si se
 * queda callado, pregunta si sigue ahí antes de soltarlos. Se apoya en
 * `LockWebService.activeLocks`, que ya concentra todos los locks sin importar por qué flujo
 * de `GridLockService` se adquirieron — de ahí que no haga falta tocar pantallas.
 *
 * En paralelo renueva los locks en silencio cada `heartbeatMs` mientras el usuario esté
 * activo: si nunca se queda callado, el diálogo no se abre y sin eso el lock envejecería
 * con la fecha de la adquisición original.
 *
 * Toda la lógica (timers, countdown, renovación, expiración) vive acá:
 * `SesionActivaComponent` solo muestra el `segundosRestantes` que este servicio calcula.
 * Se instancia desde `app.config.ts` — nadie más lo inyecta.
 */
@Injectable({ providedIn: "root" })
export class SesionActivaService implements OnDestroy {
  private readonly lockWeb = inject(LockWebService);
  private readonly resetRegistry = inject(SesionResetRegistry);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  /** Countdown que muestra el diálogo. */
  public readonly segundosRestantes = signal<number>(0);

  private inactividadMs = INACTIVIDAD_MS_DEFAULT;
  private respuestaMs = RESPUESTA_MS_DEFAULT;
  private heartbeatMs = HEARTBEAT_MS_DEFAULT;

  /**
   * Flags de coordinación interna, no estado de UI: se leen desde el tick, que corre fuera
   * de la zona de Angular justamente para no disparar change detection.
   */
  private ultimaActividadMs = 0;
  private venceRespuestaMs = 0;
  private venceHeartbeatMs = 0;
  private renovandoEnSilencio = false;
  /** Se venció el plazo y se está soltando el lock: el tick no tiene que volver a preguntar. */
  private expirando = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private dialogRef: MatDialogRef<SesionActivaComponent, boolean> | null = null;

  /**
   * Pasada la inactividad, el evento no cuenta: el tick puede no haber corrido todavía
   * (pestaña de fondo o congelada) y el mouse al volver no tiene que salvar el lock.
   */
  private readonly onActividad = (): void => {
    const ahora = Date.now();
    if (ahora - this.ultimaActividadMs >= this.inactividadMs) return;
    this.ultimaActividadMs = ahora;
  };

  private readonly onVuelveALaPestania = (): void => {
    if (document.visibilityState === "visible") this.tick();
  };

  constructor() {
    this.cargarConfig();
    effect(
      () => {
        if (this.lockWeb.activeLocks().length) this.iniciarMonitor();
        else this.detenerMonitor();
      },
      { allowSignalWrites: true },
    );
  }

  public ngOnDestroy(): void {
    this.detenerMonitor();
  }

  /** Los tiempos por cliente todavía no tienen endpoint: por ahora, los valores por defecto. */
  private cargarConfig(): void {
    this.inactividadMs = INACTIVIDAD_MS_DEFAULT;
    this.respuestaMs = RESPUESTA_MS_DEFAULT;
    this.heartbeatMs = HEARTBEAT_MS_DEFAULT;
  }

  private iniciarMonitor(): void {
    if (this.intervalId !== null) return;

    this.registrarActividad();
    this.reiniciarHeartbeat();
    // Fuera de la zona: `mousemove` y `scroll` dispararían change detection en cada evento.
    this.ngZone.runOutsideAngular(() => {
      for (const evento of EVENTOS_ACTIVIDAD) {
        document.addEventListener(evento, this.onActividad, {
          passive: true,
          capture: true,
        });
      }
      // `resume`: la pestaña vuelve de congelada (ahorro de energía / pestañas dormidas).
      document.addEventListener("visibilitychange", this.onVuelveALaPestania);
      document.addEventListener("resume", this.onVuelveALaPestania);
      this.intervalId = setInterval(() => this.tick(), TICK_MS);
    });
  }

  private detenerMonitor(): void {
    if (this.intervalId === null) return;

    for (const evento of EVENTOS_ACTIVIDAD) {
      document.removeEventListener(evento, this.onActividad, { capture: true });
    }
    document.removeEventListener("visibilitychange", this.onVuelveALaPestania);
    document.removeEventListener("resume", this.onVuelveALaPestania);
    clearInterval(this.intervalId);
    this.intervalId = null;
    this.venceHeartbeatMs = 0;
    this.renovandoEnSilencio = false;
    this.expirando = false;
    this.segundosRestantes.set(0);
    // `undefined` = lo cerró el sistema porque ya no hay lock, no el usuario ni el countdown.
    this.cerrarDialogo(undefined);
  }

  /**
   * Corre fuera de la zona de Angular. Compara contra un timestamp absoluto en vez de contar
   * ticks porque el navegador estrangula los timers en pestañas de fondo: al volver a la
   * pestaña el vencimiento se detecta igual.
   */
  private tick(): void {
    if (this.intervalId === null || this.expirando) return;
    const ahora = Date.now();

    if (this.dialogRef) {
      const restantes = this.calcularRestantes(ahora);
      this.ngZone.run(() => {
        this.segundosRestantes.set(restantes);
        if (restantes) return;
        // El cierre es asíncrono (animación): sin esto, el tick siguiente ve el diálogo
        // cerrado, el usuario sigue callado y abre otro.
        this.expirando = true;
        this.cerrarDialogo(false);
      });
      return;
    }

    const quietoMs = ahora - this.ultimaActividadMs;
    if (quietoMs >= this.inactividadMs) {
      // El plazo corre desde la última actividad, no desde que el tick lo detectó: con la
      // pestaña de fondo o congelada el tick puede llegar varios minutos tarde.
      this.venceRespuestaMs =
        this.ultimaActividadMs + this.inactividadMs + this.respuestaMs;
      this.ngZone.run(() => {
        if (this.calcularRestantes(ahora)) this.abrirDialogo();
        else this.expirar();
      });
      return;
    }

    if (ahora >= this.venceHeartbeatMs) {
      this.ngZone.run(() => this.renovarEnSilencio());
    }
  }

  private calcularRestantes(ahora: number): number {
    return Math.max(0, Math.ceil((this.venceRespuestaMs - ahora) / 1000));
  }

  private registrarActividad(): void {
    this.ultimaActividadMs = Date.now();
  }

  private reiniciarHeartbeat(): void {
    this.venceHeartbeatMs = Date.now() + this.heartbeatMs;
  }

  /**
   * Renovación de fondo del usuario que trabaja sin parar: sin diálogo y sin snackbar. El
   * `return` del vencimiento de inactividad de arriba es lo que garantiza que no se renueve
   * en el mismo tick en que se pregunta si sigue ahí.
   */
  private renovarEnSilencio(): void {
    if (this.renovandoEnSilencio) return;
    this.renovandoEnSilencio = true;
    // Reprograma antes de salir: el tick corre cada segundo y el PUT no es instantáneo.
    this.reiniciarHeartbeat();

    this.lockWeb.renovarTodos().subscribe((renovado) => {
      this.renovandoEnSilencio = false;
      // Si la renovación falla, el lock ya no es nuestro: mismo cierre que el del diálogo.
      if (!renovado) this.expirar();
    });
  }

  private abrirDialogo(): void {
    if (this.dialogRef) return;

    const totalSegundos = Math.ceil(this.respuestaMs / 1000);
    this.segundosRestantes.set(this.calcularRestantes(Date.now()));

    this.dialogRef = this.dialog.open<
      SesionActivaComponent,
      SesionActivaDialogData,
      boolean
    >(SesionActivaComponent, {
      disableClose: true,
      autoFocus: true,
      height: "auto",
      width: "400px",
      data: {
        segundosRestantes: this.segundosRestantes.asReadonly(),
        totalSegundos,
      },
    });

    this.dialogRef.afterClosed().subscribe((confirmado?: boolean) => {
      this.dialogRef = null;
      if (confirmado === true) this.confirmarPresencia();
      else if (confirmado === false) this.expirar();
    });
  }

  private cerrarDialogo(resultado: boolean | undefined): void {
    const ref = this.dialogRef;
    this.dialogRef = null;
    ref?.close(resultado);
  }

  /**
   * Si la renovación falla, el lock ya no es de este usuario: no alcanza con reiniciar el
   * timer, hay que devolver la pantalla a modo lectura igual que si hubiera expirado.
   */
  private confirmarPresencia(): void {
    this.registrarActividad();
    this.reiniciarHeartbeat();
    this.segundosRestantes.set(0);
    this.lockWeb.renovarTodos().subscribe((renovado) => {
      // Si la renovación falla, la sesión expira igual: el lock ya no es nuestro.
      if (renovado) return;
      this.expirar();
    });
  }

  private expirar(): void {
    this.expirando = true;
    this.segundosRestantes.set(0);
    this.resetRegistry.ejecutarResets();
    // Un diálogo abierto por la pantalla (ej. Evolución de la Persona) dejaría confirmar
    // cambios que el reset ya descartó. Cerrarlos emite `undefined`: cuenta como cancelar.
    this.dialog.closeAll();

    const rutaDeVuelta = this.resetRegistry.tomarRutaDeVuelta();
    if (rutaDeVuelta) {
      this.router.navigateByUrl(`/${rutaDeVuelta}`);
    }

    // Red final: los flujos que navegan liberan en el `ngOnDestroy` del hijo, y hay
    // pantallas que todavía lo hacen en `volver()` — esto barre lo que ningún reset soltó.
    setTimeout(() => this.lockWeb.liberarTodos().subscribe(), MS_GRACIA_RESET);

    this.snackBar.open(
      this.transloco.translate<string>("sesion-activa.liberado"),
      "Aceptar",
      {
        duration: 30000,
        panelClass: ["custom-snackbar", "snackbar-error"],
      },
    );
  }
}
